import React, { useState } from 'react';
import { Chip, Menu, MenuItem, ListItemText } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { lifecycleTone } from './itemStatusTones';

/**
 * ITEM-ACTIVE.4 — the item's active state, as a menu pill.
 *
 * .3 made the chip clickable and that was not enough: a chip that flips the
 * item on click looks exactly like a chip that does nothing, so the control
 * never promised its own consequence. You had to already know.
 *
 * The chevron is that promise — the one mark that says "there is something
 * behind this" — and the menu then names the act in words before anything
 * happens: "Make this item inactive". Nothing fires on the first press, which
 * is why this needs no confirm dialog for the ordinary case.
 *
 * THE PILL DOES NOT GROW. Same metrics as every neighbour on the header row
 * (0.68rem, 20px tall, weight 600, the same alpha-tint recipe) because it is
 * still a status first and a control second — the chevron is 14px and tucked
 * inside the existing height. A control that had to get bigger to be noticed
 * would have lost the argument.
 *
 * NON-ADMIN: no chevron, no menu, no pointer. Identical to the chip that
 * shipped before any of this.
 */

export interface ItemStatusChipProps {
  active: boolean;
  canEdit: boolean;
  onDeactivate?: () => void;
  onReactivate?: () => void;
  busy?: boolean;
}

const ItemStatusChip: React.FC<ItemStatusChipProps> = ({
  active, canEdit, onDeactivate, onReactivate, busy = false,
}) => {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const handler = active ? onDeactivate : onReactivate;
  const interactive = canEdit && Boolean(handler) && !busy;

  // ITEM-ACTIVE.4b — the lifecycle chip speaks the app's status dialect:
  // Active in the OK family, Inactive in the Out family. It was the neutral
  // tint, which made the ordinary case invisible on a row already carrying
  // coloured stock pills — the eye read the greens and reds and skipped the
  // one grey chip.
  const tone = lifecycleTone(active);

  const close = () => setAnchor(null);
  const choose = () => { close(); handler?.(); };

  return (
    <>
      <Chip
        label={active ? 'Active' : 'Inactive'}
        size="small"
        clickable={interactive}
        onClick={interactive ? (e => setAnchor(e.currentTarget)) : undefined}
        // The chevron rides inside the chip's existing height rather than
        // beside it, so the row's rhythm is untouched.
        deleteIcon={interactive ? <ArrowDropDownIcon /> : undefined}
        onDelete={interactive ? (e => setAnchor(e.currentTarget.parentElement as HTMLElement)) : undefined}
        aria-haspopup={interactive ? 'menu' : undefined}
        aria-expanded={interactive ? Boolean(anchor) : undefined}
        aria-label={interactive
          ? (active ? 'Active — change item status' : 'Inactive — change item status')
          : undefined}
        sx={{
          fontSize: '0.68rem',
          height: 20,
          fontWeight: 600,
          color: tone.color,
          backgroundColor: tone.bg,
          border: 'none',
          // ITEM-ACTIVE.4b — optics. MUI gives a chip asymmetric padding once
          // it carries a delete icon (roomy on the left, tight on the right),
          // which made the pill sit visibly off-centre next to its neighbours.
          // Padding is set symmetrically here and the chevron is spaced as part
          // of the label rather than crowded against the edge.
          pl: 1,
          pr: 0.75,
          '& .MuiChip-label': { px: 0 },
          '& .MuiChip-deleteIcon': {
            fontSize: 14,
            margin: 0,
            marginLeft: '2px',
            color: tone.color,
            '&:hover': { color: tone.color },
          },
          ...(interactive
            ? {
              cursor: 'pointer',
              '&:hover': { filter: 'brightness(0.96)' },
              '&:focus-visible': { outline: '2px solid', outlineColor: tone.color },
            }
            : {}),
        }}
      />

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {/* Named in words, so the consequence is read before it happens. */}
        <MenuItem onClick={choose}>
          <ListItemText
            primary={active ? 'Make this item inactive' : 'Reactivate item'}
            secondary={active
              ? 'Hides it from lists and pickers. History is kept.'
              : 'Puts it back on lists and pickers.'}
            primaryTypographyProps={{ fontSize: 14 }}
            secondaryTypographyProps={{ fontSize: 12 }}
          />
        </MenuItem>
      </Menu>
    </>
  );
};

export default ItemStatusChip;
